import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface RecurrenceState {
  freq: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  /** Dias da semana (0=dom ... 6=sáb) — usado quando freq === 'weekly'. */
  byWeekdays: number[];
  /** Mensal: no dia fixo do mês ou na N-ésima ocorrência do dia da semana. */
  monthlyMode: 'day' | 'weekday';
  /** Posição no mês (1..4, -1 = última) — usado com monthlyMode === 'weekday'. */
  monthlyPos: number;
  end: 'never' | 'count' | 'until';
  count: number;
  /** Data final (YYYY-MM-DD) — usado com end === 'until'. */
  until: string;
}

export const DEFAULT_RECURRENCE: RecurrenceState = {
  freq: 'none',
  interval: 1,
  byWeekdays: [],
  monthlyMode: 'day',
  monthlyPos: 1,
  end: 'never',
  count: 10,
  until: '',
};

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;
const WEEKDAY_SHORT = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;
const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'] as const;

const FREQ_OPTIONS = [
  { value: 'none', label: 'Não se repete' },
  { value: 'daily', label: 'Todos os dias' },
  { value: 'weekly', label: 'Toda semana' },
  { value: 'monthly', label: 'Todo mês' },
  { value: 'yearly', label: 'Todo ano' },
] as const;

const FREQ_UNIT: Record<Exclude<RecurrenceState['freq'], 'none'>, [string, string]> = {
  daily: ['dia', 'dias'],
  weekly: ['semana', 'semanas'],
  monthly: ['mês', 'meses'],
  yearly: ['ano', 'anos'],
};

const POS_LABELS: Array<{ value: number; label: string }> = [
  { value: 1, label: '1ª' },
  { value: 2, label: '2ª' },
  { value: 3, label: '3ª' },
  { value: 4, label: '4ª' },
  { value: -1, label: 'última' },
];

/** Constrói a string RRULE a partir do estado (null quando não se repete). */
export function buildRRuleString(state: RecurrenceState, startDate: Date | null): string | null {
  if (state.freq === 'none') return null;

  const parts: string[] = [`FREQ=${state.freq.toUpperCase()}`];
  if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);

  if (state.freq === 'weekly') {
    const days = state.byWeekdays.length > 0
      ? state.byWeekdays
      : startDate
        ? [startDate.getDay()]
        : [];
    if (days.length > 0) {
      parts.push(`BYDAY=${[...days].sort((a, b) => a - b).map(d => WEEKDAY_CODES[d]).join(',')}`);
    }
  }

  if (state.freq === 'monthly' && state.monthlyMode === 'weekday' && startDate) {
    parts.push(`BYDAY=${state.monthlyPos}${WEEKDAY_CODES[startDate.getDay()]}`);
  }

  if (state.end === 'count' && state.count >= 2) {
    parts.push(`COUNT=${state.count}`);
  } else if (state.end === 'until' && state.until) {
    const compact = state.until.replace(/-/g, '');
    parts.push(`UNTIL=${compact}T235959Z`);
  }

  return parts.join(';');
}

/** Interpreta uma string RRULE (armazenada no backend) de volta para o estado. */
export function parseRRuleString(rrule: string | null | undefined): RecurrenceState {
  if (!rrule) return { ...DEFAULT_RECURRENCE };

  const state: RecurrenceState = { ...DEFAULT_RECURRENCE, byWeekdays: [] };

  for (const part of rrule.replace(/^RRULE:/i, '').split(';')) {
    const [rawKey, rawValue] = part.split('=');
    if (!rawKey || !rawValue) continue;
    const key = rawKey.trim().toUpperCase();
    const value = rawValue.trim().toUpperCase();

    switch (key) {
      case 'FREQ': {
        const freq = value.toLowerCase();
        if (freq === 'daily' || freq === 'weekly' || freq === 'monthly' || freq === 'yearly') {
          state.freq = freq;
        }
        break;
      }
      case 'INTERVAL': {
        const interval = Number(value);
        if (Number.isInteger(interval) && interval >= 1) state.interval = interval;
        break;
      }
      case 'BYDAY': {
        const entries = value.split(',');
        const hasPosition = entries.some(entry => /^-?\d/.test(entry));
        if (hasPosition) {
          const match = /^(-?\d+)([A-Z]{2})$/.exec(entries[0]);
          if (match) {
            state.monthlyMode = 'weekday';
            state.monthlyPos = Number(match[1]);
          }
        } else {
          state.byWeekdays = entries
            .map(code => WEEKDAY_CODES.indexOf(code as typeof WEEKDAY_CODES[number]))
            .filter(index => index >= 0);
        }
        break;
      }
      case 'BYSETPOS': {
        const pos = Number(value);
        if (Number.isInteger(pos)) {
          state.monthlyMode = 'weekday';
          state.monthlyPos = pos;
        }
        break;
      }
      case 'COUNT': {
        const count = Number(value);
        if (Number.isInteger(count) && count >= 2) {
          state.end = 'count';
          state.count = count;
        }
        break;
      }
      case 'UNTIL': {
        const match = /^(\d{4})(\d{2})(\d{2})/.exec(value);
        if (match) {
          state.end = 'until';
          state.until = `${match[1]}-${match[2]}-${match[3]}`;
        }
        break;
      }
      default:
        break;
    }
  }

  return state;
}

/** Resumo em pt-BR da regra montada. */
export function describeRecurrence(state: RecurrenceState, startDate: Date | null): string {
  if (state.freq === 'none') return 'Não se repete';

  const [singular, plural] = FREQ_UNIT[state.freq];
  let text = state.interval > 1 ? `A cada ${state.interval} ${plural}` : `A cada ${singular}`;

  if (state.freq === 'weekly') {
    const days = state.byWeekdays.length > 0
      ? state.byWeekdays
      : startDate
        ? [startDate.getDay()]
        : [];
    if (days.length > 0) {
      text += ` (${[...days].sort((a, b) => a - b).map(d => WEEKDAY_NAMES[d]).join(', ')})`;
    }
  }

  if (state.freq === 'monthly' && state.monthlyMode === 'weekday' && startDate) {
    const pos = POS_LABELS.find(p => p.value === state.monthlyPos)?.label ?? `${state.monthlyPos}ª`;
    text += ` — na ${pos} ${WEEKDAY_NAMES[startDate.getDay()]}`;
  } else if (state.freq === 'monthly' && startDate) {
    text += ` — no dia ${startDate.getDate()}`;
  }

  if (state.end === 'count') {
    text += ` · ${state.count} vezes`;
  } else if (state.end === 'until' && state.until) {
    const [year, month, day] = state.until.split('-');
    text += ` · até ${day}/${month}/${year}`;
  } else {
    text += ' · sem data final';
  }

  return text;
}

interface Props {
  value: RecurrenceState;
  onChange: (next: RecurrenceState) => void;
  startDate: Date | null;
  disabled?: boolean;
}

export function RecurrenceEditor({ value, onChange, startDate, disabled = false }: Props) {
  const set = (patch: Partial<RecurrenceState>) => onChange({ ...value, ...patch });

  const toggleWeekday = (day: number) => {
    const current = value.byWeekdays.length > 0
      ? value.byWeekdays
      : startDate
        ? [startDate.getDay()]
        : [];
    // Recorrência semanal precisa de pelo menos um dia — sem isso o clique em desmarcar o
    // último dia visível não teria efeito nenhum (o fallback acima reapareceria no próximo
    // render), então bloqueamos aqui em vez de deixar a ação parecer travada silenciosamente.
    if (current.includes(day) && current.length <= 1) return;
    const next = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day];
    set({ byWeekdays: next });
  };

  const effectiveWeekdays = value.byWeekdays.length > 0
    ? value.byWeekdays
    : startDate && value.freq === 'weekly'
      ? [startDate.getDay()]
      : [];

  return (
    <div className="space-y-3">
      <Select
        value={value.freq}
        onValueChange={(freq) => set({ freq: freq as RecurrenceState['freq'] })}
      >
        <SelectTrigger disabled={disabled}>
          <SelectValue placeholder="Selecione..." />
        </SelectTrigger>
        <SelectContent>
          {FREQ_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value.freq !== 'none' && (
        <>
          <div className="flex items-center gap-2">
            <Label htmlFor="recorrencia-intervalo" className="text-xs whitespace-nowrap">
              A cada
            </Label>
            <Input
              id="recorrencia-intervalo"
              type="number"
              min={1}
              max={99}
              className="h-9 w-20"
              value={String(value.interval)}
              onChange={(e) => {
                const interval = Number(e.target.value);
                set({ interval: Number.isInteger(interval) && interval >= 1 ? Math.min(interval, 99) : 1 });
              }}
              disabled={disabled}
            />
            <span className="text-xs text-muted-foreground">
              {value.interval > 1 ? FREQ_UNIT[value.freq][1] : FREQ_UNIT[value.freq][0]}
            </span>
          </div>

          {value.freq === 'weekly' && (
            <div className="flex gap-1">
              {WEEKDAY_SHORT.map((label, day) => (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleWeekday(day)}
                  title={WEEKDAY_NAMES[day]}
                  className={cn(
                    'h-8 w-8 rounded-full border text-xs font-medium transition-colors',
                    effectiveWeekdays.includes(day)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted',
                    disabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {value.freq === 'monthly' && (
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={value.monthlyMode}
                onValueChange={(monthlyMode) => set({ monthlyMode: monthlyMode as 'day' | 'weekday' })}
              >
                <SelectTrigger className="h-9 w-auto min-w-44" disabled={disabled}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">
                    {startDate ? `No dia ${startDate.getDate()}` : 'No dia do mês'}
                  </SelectItem>
                  <SelectItem value="weekday">
                    {startDate ? `Na ocorrência de ${WEEKDAY_NAMES[startDate.getDay()]}` : 'No dia da semana'}
                  </SelectItem>
                </SelectContent>
              </Select>

              {value.monthlyMode === 'weekday' && (
                <Select
                  value={String(value.monthlyPos)}
                  onValueChange={(pos) => set({ monthlyPos: Number(pos) })}
                >
                  <SelectTrigger className="h-9 w-28" disabled={disabled}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POS_LABELS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Termina</Label>
            <Select
              value={value.end}
              onValueChange={(end) => set({ end: end as RecurrenceState['end'] })}
            >
              <SelectTrigger className="h-9 w-40" disabled={disabled}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Nunca</SelectItem>
                <SelectItem value="count">Após N vezes</SelectItem>
                <SelectItem value="until">Em uma data</SelectItem>
              </SelectContent>
            </Select>

            {value.end === 'count' && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={2}
                  max={366}
                  className="h-9 w-20"
                  value={String(value.count)}
                  onChange={(e) => {
                    const count = Number(e.target.value);
                    set({ count: Number.isInteger(count) ? count : 2 });
                  }}
                  disabled={disabled}
                />
                <span className="text-xs text-muted-foreground">vezes</span>
              </div>
            )}

            {value.end === 'until' && (
              <Input
                type="date"
                className="h-9 w-40"
                value={value.until}
                onChange={(e) => set({ until: e.target.value })}
                disabled={disabled}
              />
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {describeRecurrence(value, startDate)}
          </p>
        </>
      )}
    </div>
  );
}
